import { useState } from 'react';
import { Download } from 'lucide-react';
import { FilingGranularity, exportDeltaManualPortalNote } from '@signalvote/shared';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useExportDelta } from '@/hooks/queries/useFinanceReports';
import { portalNameFor } from './amendmentWorkflowHint';
import { triggerBrowserDownload } from './downloadHelpers';

interface ExportDeltaButtonProps {
  report: ComplianceReportDetailDto;
  /**
   * Campaign's resolved state code (ADR-022 — the sole jurisdiction source of
   * truth). Drives the registered-format lookup; null = not configured.
   */
  stateCode: string | null;
  /**
   * §E — visual prominence. For an append-only (PortalOnly / Alabama)
   * jurisdiction Export is the recommended path, so the caller promotes it to
   * `'primary'` (filled green). Defaults to `'secondary'` (outline) for every
   * other placement, unchanged from before.
   */
  variant?: 'primary' | 'secondary';
}

/**
 * Builds and downloads the Alabama EDI incremental delta: the period's
 * not-yet-uploaded Confirmed transactions. The AL FCPA portal is append-only
 * (ADR-020), so each transaction uploads exactly once — re-uploading would
 * duplicate it.
 *
 * Rendered for Incremental jurisdictions only — read off the SERVED granularity
 * (`profile.granularity`, the same axis the export-delta endpoint gates on), NOT the amendment model
 * (NJ amends PortalOnly but files whole reports, so export-delta refuses it), NOT re-derived from the
 * report's FormatKind and NOT a hard-coded `stateCode === 'AL'` default — so a future Incremental
 * state gets the button by declaring its ruleset, and a non-Incremental jurisdiction never sees it.
 */
export function ExportDeltaButton({ report, stateCode, variant = 'secondary' }: ExportDeltaButtonProps) {
  const { data: profile } = useComplianceProfile();
  const { exportDelta } = useExportDelta();

  const [busy, setBusy] = useState(false);
  const [nothingToExport, setNothingToExport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isIncremental = profile?.granularity === FilingGranularity.Incremental;
  if (!stateCode || !isIncremental) {
    return null;
  }

  const handleExport = async () => {
    setBusy(true);
    setError(null);
    setNothingToExport(null);
    try {
      const result = await exportDelta(report.id);
      if (result.exported) {
        triggerBrowserDownload(result.fileBlob, result.fileName);
      } else {
        // §32 — the backend no-op message says "everything has already been uploaded",
        // but that's a lie when the only leftover rows are Confirmed in-kind "Other"
        // that the EDI can't carry (they never enter the export set). Redirect the
        // treasurer to the hand-entry panel below instead of implying the work is done.
        const manualPortalLeftover = report.contributionItems.filter(
          (i) => i.requiresManualPortalEntry && i.ledgerStatus === 'Confirmed',
        ).length;
        setNothingToExport(
          manualPortalLeftover > 0
            ? exportDeltaManualPortalNote(manualPortalLeftover, portalNameFor(stateCode))
            : result.message,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export new transactions');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant={variant === 'primary' ? 'default' : 'outline'}
        onClick={handleExport}
        disabled={busy}
        data-testid="export-delta-button"
        className={variant === 'primary' ? 'h-auto min-h-9 w-full whitespace-normal py-2' : undefined}
      >
        <Download className="mr-1 h-4 w-4" />
        {busy ? 'Exporting…' : 'Export new transactions for this period'}
      </Button>
      {nothingToExport && (
        <p className="text-sm text-muted-foreground" data-testid="export-delta-empty">
          {nothingToExport}
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="export-delta-error">
          {error}
        </p>
      )}
    </div>
  );
}
